// Fee calculations for preview in the ticket editor.
//
// This is a slightly simplified version of the pricing service used on the server.
// This version does not support multipe tickets per order. The Ruby version is the source
// of truth used during an actual checkout transaction.
//
// NOTE: If you change any logic here, make sure to update the Ruby version in
// app/services/registration/pricing_service.rb as well. The two versions must be kept in sync.

/**
 * Calculate fees for a ticket
 *
 * Calculates the fees for a ticket based on the amount, the customer pays fees, and the fee settings.
 * Assumes that any per-order fees are for an order of only the ticket.
 *
 * @param {number} amount - The amount in cents to calculate fees for
 * @param {boolean} customerPaysFees - Whether the customer pays the fees (true) or organizer pays (false)
 * @param {Object} feeSettings - A hash of fee definitions. each is a hash of  {base: base fee, percent: percentage amount}
 * @param {Object} feeSettings.localist - Localist per-order fees
 * @param {Object} feeSettings.payment - Payment provider per-order fees
 * @param {Object} feeSettings.localist_item - Localist per-item fees
 * @returns {number} The calculated fee amount in cents
 */
export const calculateFee = (amount, customerPaysFees, feeSettings) => {
  if (amount <= 0) {
    return 0;
  }

  return customerPaysFees
    ? calculateCustomerPaidFees(amount, feeSettings)
    : calculateOrganizerPaidFees(amount, feeSettings);
};

/**
 * Calculate fees when the customer pays the fees
 *
 * The amount charged needs to be increased to net the organizer +amount+ after all fees
 * are subtracted.
 *
 * The +amount+ is face value of the ticket.
 *
 * @param {number} amount - The amount in cents to calculate fees for.
 * @param {Object} feeSettings - A hash of fee definitions. each is a hash of  {base: base fee, percent: percentage amount}
 * @returns {number} The calculated fee amount in cents
 */
const calculateCustomerPaidFees = (amount, feeSettings) => {
  // Increase the charged amount to ensure +amount+ is the net amount after
  // subtracting all fees.
  //
  // The per item fee is a fixed amount and does not scale with the total charge.
  const localistItemFee = calculateLocalistItemFee(amount, feeSettings);
  const numerator =
    feeSettings.payment.base +
    feeSettings.localist.base +
    amount +
    localistItemFee;
  const denominator =
    feeSettings.payment.percent + feeSettings.localist.percent - 1;
  const chargedAmount = Math.round(-(numerator / denominator));

  return Math.round(
    calculateLocalistFee(chargedAmount, feeSettings) +
      calculatePaymentProcessorFee(chargedAmount, feeSettings) +
      localistItemFee
  );
};

/**
 * Calculate fees when the organizer pays the fees.
 *
 * @param {number} amount - The amount in cents to calculate fees for.
 * @param {Object} feeSettings - A hash of fee definitions. each is a hash of  {base: base fee, percent: percentage amount}
 * @returns {number} The calculated fee amount in cents
 */
const calculateOrganizerPaidFees = (amount, feeSettings) => {
  return Math.round(
    calculateLocalistItemFee(amount, feeSettings) +
      calculateLocalistFee(amount, feeSettings) +
      calculatePaymentProcessorFee(amount, feeSettings)
  );
};

// Individual Fee Calculations

/**
 * Calculate Localist's per-item fee
 *
 * @param {number} amount - The amount in cents to calculate fees for.
 * @param {Object} feeSettings - A hash of fee definitions. each is a hash of  {base: base fee, percent: percentage amount}
 * @returns {number} The calculated fee amount in cents
 */
// Localist's per-item fee
const calculateLocalistItemFee = (amount, feeSettings) => {
  return Math.round(
    feeSettings.localist_item.base + amount * feeSettings.localist_item.percent
  );
};

/**
 * Calculate Localist's per-transaction fee
 *
 * The amount should include any fees the customer is charged (via "Customer Pays Fees" option).
 *
 * @param {number} amount - The amount in cents to calculate fees for.
 * @param {Object} feeSettings - A hash of fee definitions. each is a hash of  {base: base fee, percent: percentage amount}
 * @returns {number} The calculated fee amount in cents
 */
const calculateLocalistFee = (amount, feeSettings) => {
  return Math.round(
    feeSettings.localist.base + amount * feeSettings.localist.percent
  );
};

/**
 * Calculate Payment processor's per-transaction fee
 *
 * The amount should include any fees the customer is charged (via "Customer Pays Fees" option).
 *
 * @param {number} amount - The amount in cents to calculate fees for.
 * @param {Object} feeSettings - A hash of fee definitions. each is a hash of  {base: base fee, percent: percentage amount}
 * @returns {number} The calculated fee amount in cents
 */
const calculatePaymentProcessorFee = (amount, feeSettings) => {
  return Math.round(
    feeSettings.payment.base + amount * feeSettings.payment.percent
  );
};
